import { Component, inject, OnDestroy, OnInit } from '@angular/core';
import { AtAGlanceNotification, Study } from '@models';
import {
  AppHeaderService,
  NotificationFilterService,
  PortfolioLoadingService,
  PortfolioStatsService,
  SidebarService,
  StudyFilterService,
  StudyService
} from '@services';
import { Subscription } from 'rxjs';

@Component({
  selector: 'home',
  templateUrl: './home.component.html',
  styleUrls: ['./home.component.scss'],
  host: {
    class: 'centered-page-container'
  }
})
export class HomeComponent implements OnInit, OnDestroy {
  isLoading = false;
  sidebarCollapsed = true;
  feedbackHeightPx: number | null = null;

  private readonly desktopBreakpoint = 1200;
  private sub = new Subscription();
  private sidebar = inject(SidebarService);
  private studyService = inject(StudyService);
  private portfolioStatsService = inject(PortfolioStatsService);
  private portfolioLoadingService = inject(PortfolioLoadingService);
  private appHeaderService = inject(AppHeaderService);
  private notificationFilterService = inject(NotificationFilterService);
  private studyFilterService = inject(StudyFilterService);
  private requestsSub = new Subscription();
  private allNotifications: AtAGlanceNotification[] = [];
  notifications: AtAGlanceNotification[] = [];

  ngOnInit(): void {
    this.sub.add(this.sidebar.sidebarCollapsed$.subscribe((v) => (this.sidebarCollapsed = v)));

    const initialSponsor = this.appHeaderService.getCurrentHeaderState().selectedSponsor;

    this.requestsSub.unsubscribe();
    this.requestsSub = new Subscription();
    this.loadPortfolioData(initialSponsor);
    this.loadAtAGlanceNotifications(initialSponsor);
    this.sub.add(
      this.appHeaderService.getSponsorApplied().subscribe((sponsor) => {
        this.requestsSub.unsubscribe();
        this.requestsSub = new Subscription();
        this.loadPortfolioData(sponsor);
        this.loadAtAGlanceNotifications(sponsor);
      })
    );
  }

  ngOnDestroy(): void {
    this.sub.unsubscribe();
    this.requestsSub.unsubscribe();
  }

  private loadPortfolioData(sponsorSelected: string): void {
    this.portfolioLoadingService.setLoading(true);
    const parentClientNames = this.studyService.getParentClientNames();
    const clientNamesToUse =
      sponsorSelected && sponsorSelected !== 'All' ? [sponsorSelected] : parentClientNames;

    this.requestsSub.add(
      this.studyService.getStudiesPortfolio(clientNamesToUse).subscribe({
        next: (studies: Study[]) => {
          // Calculate stats with the new data
          this.portfolioStatsService.calculatePortfolioStats(studies);

          // Apply or remove client filter based on sponsor selection
          if (sponsorSelected && sponsorSelected !== 'All') {
            this.studyFilterService.addClientFilter(true, sponsorSelected);
          } else {
            // Remove client filter when switching to 'All'
            this.studyFilterService.removeClientFilter();
          }

          this.portfolioLoadingService.setLoading(false);
        },
        error: (error) => {
          console.error('Error fetching portfolio data:', error);
          this.portfolioLoadingService.setLoading(false);
        }
      })
    );
  }

  private loadAtAGlanceNotifications(sponsorSelected: string): void {
    const parentClientNames = this.studyService.getParentClientNames();

    this.requestsSub.add(
      this.studyService.getAtAGlanceNotifications(parentClientNames).subscribe({
        next: (notifications) => {
          this.allNotifications = notifications;
          this.applyNotificationsFilter(sponsorSelected);
        },
        error: (error) => {
          console.error('Error fetching at-a-glance notifications:', error);
        }
      })
    );
  }

  private applyNotificationsFilter(sponsorSelected: string): void {
    this.notifications = this.notificationFilterService.filterBySponsor(
      this.allNotifications,
      sponsorSelected
    );
  }
}
